import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
} from '@material-ui/core';
import {
  ISupplierUserProfile,
  IUser,
  ICOA,
  IcoaTransactions,
} from 'constants/interfaces';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Import images for wallet designs (even if unused, left for reference)

// Import your SCSS for styling
import './Overview.scss';
import {faUpload} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

interface OverviewProps {
  user: ISupplierUserProfile;
  customerData: IUser | null;
  accounts: ICOA[];
  transactions: IcoaTransactions[];
}

function Overview({user, customerData, accounts, transactions}: OverviewProps) {
  if (!customerData) {
    return <Typography>Loading overview data...</Typography>;
  }

  /** ******************************************************
   * 1) TOP EXPENSE CATEGORIES (by account_name)
   *    Summing negative transactions.
   ********************************************************/
  const expenseMap: {[accountName: string]: number} = {};
  transactions
    .filter((t) => t.amount < 0)
    .forEach((t) => {
      const account = accounts.find((a) => a._id === t.account_id);
      // Use the account_name if found, otherwise "Other"
      const accountName = account ? account.account_name : 'Other';
      if (!expenseMap[accountName]) {
        expenseMap[accountName] = 0;
      }
      expenseMap[accountName] += Math.abs(t.amount);
    });

  const expenseCategories = Object.keys(expenseMap).map((accountName) => ({
    accountName,
    total: expenseMap[accountName],
  }));
  expenseCategories.sort((a, b) => b.total - a.total);
  const topExpenseCategories = expenseCategories.slice(0, 5);

  /** ******************************************************
   * 2) MONTHLY INCOME VS EXPENSES (Bar Graph)
   ********************************************************/
  interface MonthlyData {
    month: string;
    income: number;
    expense: number;
  }
  const monthlyDataMap: {[key: string]: MonthlyData} = {};
  transactions.forEach((t) => {
    const date = new Date(t.date);
    const monthKey = `${date.getFullYear()}-${String(
      date.getMonth() + 1,
    ).padStart(2, '0')}`;
    if (!monthlyDataMap[monthKey]) {
      monthlyDataMap[monthKey] = {month: monthKey, income: 0, expense: 0};
    }
    if (t.amount > 0) {
      monthlyDataMap[monthKey].income += t.amount;
    } else {
      monthlyDataMap[monthKey].expense += Math.abs(t.amount);
    }
  });
  const monthlyData = Object.values(monthlyDataMap).sort((a, b) =>
    a.month.localeCompare(b.month),
  );

  return (
    <Box p={2} className="overview-container">
      {/* Header with title and Upload Transactions button */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" className="overview-title">
          Overview
        </Typography>

        {/* Updated Upload Transactions button */}
        <Button variant="contained" className="upload-transactions-btn">
          <FontAwesomeIcon className="fa fa-upload" icon={faUpload} />
          Upload Transactions
        </Button>
      </Box>

      {/* Two-column layout for data */}
      <Box mt={4}>
        <Grid container spacing={4}>
          {/* Left Column: Monthly Bar Graph */}
          <Grid item xs={12} md={6}>
            <Paper style={{padding: 16}}>
              <Typography variant="subtitle1" gutterBottom>
                Monthly Income vs Expenses
              </Typography>
              {monthlyData.length === 0 ? (
                <Typography>No monthly transaction data available.</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={monthlyData}
                    margin={{top: 20, right: 30, left: 20, bottom: 5}}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="month"
                      tick={{fill: '#013333', fontSize: 12}}
                    />
                    <YAxis tick={{fill: '#013333', fontSize: 12}} />
                    <Tooltip />
                    <Legend wrapperStyle={{color: '#013333', fontSize: 12}} />
                    <Bar dataKey="income" fill="#267373" name="Income" />
                    <Bar dataKey="expense" fill="#97180C" name="Expenses" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>

          {/* Right Column: Top Expense Categories */}
          <Grid item xs={12} md={6}>
            <Paper style={{padding: 16}}>
              <Typography variant="subtitle1" gutterBottom>
                Top Expense Categories
              </Typography>
              {topExpenseCategories.length === 0 ? (
                <Typography>No expense data available.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Account Name</TableCell>
                        <TableCell align="right">Total Expense (R)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topExpenseCategories.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{item.accountName}</TableCell>
                          <TableCell align="right">
                            {item.total.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default Overview;
